/** CDR detail dialog — full record + FreeSWITCH channel variables.
 *
 * Opens on row click from the CDR browser. Fetches /cdrs/{call_id}/detail
 * and renders a call summary, CGRates billing result, SIP/network info and
 * a searchable list of all raw channel variables (percent-decoded for
 * readability; FS stores e.g. "RALS_ERROR%3AACCOUNT_NOT_FOUND").
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";
import { cdrsApi } from "../../api/client";
import { CdrDetail } from "../../types/cdr";
import { useNotify } from "../common/NotificationProvider";
import { formatDuration, formatCurrency } from "../../utils/format";

interface CdrDetailDialogProps {
  open: boolean;
  callId: string | null;
  onClose: () => void;
}

/** Safely percent-decode an FS channel-variable value (raw string fallback). */
export function decodeFsValue(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Status chip: success green, auth/routing failures red with Q850 hint. */
function DetailStatusChip({ cause, q850 }: { cause: string | null; q850?: string }) {
  if (!cause) return <Chip label="Unknown" size="small" color="default" />;
  const isSuccess = cause === "NORMAL_CLEARING";
  return (
    <Chip
      label={q850 ? `${cause} (Q850 ${q850})` : cause}
      size="small"
      color={isSuccess ? "success" : "error"}
    />
  );
}

/** Key/value row used across the summary sections. */
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{ width: 200, color: "text.secondary", verticalAlign: "top" }}
      >
        {label}
      </TableCell>
      <TableCell sx={{ wordBreak: "break-all" }}>{value}</TableCell>
    </TableRow>
  );
}

/** CDR detail dialog component. */
export default function CdrDetailDialog({ open, callId, onClose }: CdrDetailDialogProps) {
  const notify = useNotify();
  const [detail, setDetail] = useState<CdrDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [fieldFilter, setFieldFilter] = useState("");

  /** Fetch the CDR detail when the dialog opens. */
  useEffect(() => {
    if (!open || !callId) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    setFieldFilter("");
    cdrsApi
      .detail(callId)
      .then((res) => {
        if (!cancelled) setDetail(res.data);
      })
      .catch((err: any) => {
        if (!cancelled) {
          notify(err.response?.data?.detail || "Failed to load CDR detail", "error");
          onClose();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, callId]);

  /** Grouped channel variables, percent-decoded and filtered. */
  const fieldGroups = useMemo(() => {
    const cf = detail?.custom_fields ?? {};
    const decoded: Record<string, string> = {};
    for (const [k, v] of Object.entries(cf)) {
      decoded[k] = decodeFsValue(String(v));
    }
    const q = fieldFilter.trim().toLowerCase();
    const matches = ([k, v]: [string, string]) =>
      !q || k.toLowerCase().includes(q) || v.toLowerCase().includes(q);

    const cgrKeys = Object.keys(decoded).filter((k) => k.startsWith("cgr_"));
    const sipKeys = Object.keys(decoded).filter(
      (k) => k.startsWith("sip_") || k.startsWith("rtp_") || k.includes("codec")
    );
    const timingKeys = Object.keys(decoded).filter(
      (k) =>
        k.endsWith("_epoch") ||
        k.endsWith("_stamp") ||
        k.endsWith("_msec") ||
        k.endsWith("_usec") ||
        k.endsWith("_sec") ||
        ["duration", "billsec", "waitsec", "mduration", "flow_billsec"].includes(k)
    );
    const grouped = new Set([...cgrKeys, ...sipKeys, ...timingKeys]);
    const otherKeys = Object.keys(decoded).filter((k) => !grouped.has(k));

    return {
      billing: cgrKeys.filter((k) => matches([k, decoded[k]])).sort(),
      sip: sipKeys.filter((k) => matches([k, decoded[k]])).sort(),
      timing: timingKeys.filter((k) => matches([k, decoded[k]])).sort(),
      other: otherKeys.filter((k) => matches([k, decoded[k]])).sort(),
      total: Object.keys(decoded).length,
      decoded,
    };
  }, [detail, fieldFilter]);

  /** Render one group of channel variables. */
  const renderFieldGroup = (title: string, keys: string[]) => {
    if (keys.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          {title} ({keys.length})
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {keys.map((k) => {
                const isError = k === "cgr_notify" && /ERROR|NOT_FOUND|DENIED|FAIL/i.test(fieldGroups.decoded[k]);
                return (
                  <InfoRow
                    key={k}
                    label={k}
                    value={
                      isError ? (
                        <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                          {fieldGroups.decoded[k]}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                        >
                          {fieldGroups.decoded[k]}
                        </Typography>
                      )
                    }
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const cf = detail?.custom_fields ?? {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            CDR Detail
          </Typography>
          {callId && (
            <Typography
              variant="caption"
              component="div"
              sx={{ fontFamily: "monospace", color: "text.secondary" }}
            >
              {callId}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : detail ? (
          <>
            {/* Call summary */}
            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm>
                  <Typography variant="body1">
                    <strong>{detail.caller || detail.caller_id || "-"}</strong>
                    {" → "}
                    <strong>{detail.callee || detail.callee_id || "-"}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detail.start_time && format(new Date(detail.start_time), "yyyy-MM-dd HH:mm:ss")}
                    {detail.answer_time
                      ? ` · answered ${format(new Date(detail.answer_time), "HH:mm:ss")}`
                      : " · not answered"}
                    {detail.end_time && ` · ended ${format(new Date(detail.end_time), "HH:mm:ss")}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <DetailStatusChip
                      cause={detail.hangup_cause}
                      q850={cf["hangup_cause_q850"]}
                    />
                    <Chip label={`Duration ${formatDuration(detail.duration)}`} size="small" variant="outlined" />
                    <Chip label={`Billable ${formatDuration(detail.billsec)}`} size="small" variant="outlined" />
                    {detail.cost != null && (
                      <Chip label={`Cost ${formatCurrency(detail.cost, 4)}`} size="small" variant="outlined" />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Core columns */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Record
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableBody>
                  <InfoRow label="Account" value={
                    detail.account ? (
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {detail.account}
                      </Typography>
                    ) : null
                  } />
                  <InfoRow label="Caller ID" value={detail.caller_id} />
                  <InfoRow label="Dialed Number" value={detail.callee_id} />
                  <InfoRow label="SIP Status" value={detail.sip_status} />
                  <InfoRow label="Carrier" value={detail.carrier} />
                  <InfoRow label="Carrier IP" value={detail.carrier_ip} />
                  <InfoRow label="Destination" value={detail.destination} />
                  <InfoRow label="Account Code" value={detail.account_code} />
                  <InfoRow label="Recording" value={detail.recording} />
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ mb: 2 }} />

            {/* Raw channel variables */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                FreeSWITCH channel variables ({fieldGroups.total})
              </Typography>
              <TextField
                size="small"
                placeholder="Filter fields…"
                value={fieldFilter}
                onChange={(e) => setFieldFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 240 }}
              />
            </Box>
            {renderFieldGroup("CGRates billing", fieldGroups.billing)}
            {renderFieldGroup("SIP / RTP / media", fieldGroups.sip)}
            {renderFieldGroup("Timing", fieldGroups.timing)}
            {renderFieldGroup("Other", fieldGroups.other)}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
